import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Autocomplete,
  TextField,
  MenuItem,
} from "@mui/material";
import {
  createDocumentList,
  updateDocumentList,
  deleteDocumentList,
  buildRegisterDocNumber,
  getRegisterDocSource,
  resolveDocumentUid,
  resolveDocumentNameAbbreviation,
  ensureNumberedDocumentUid,
  documentAbbreviations,
} from "./docListAPI";
import { secondAxios } from "../../../../../../../api/axios";
import EditableTable from "../../../../../../../components/Tables/EditableTable";
import { useDCS } from "../DCSContext";
import { fetchPlantProjectChoices } from "../../UUIDRegister/uuidRegisterApi";

const abbreviationByCode = new Map(
  documentAbbreviations.map((item) => [
    String(item.abbreviation ?? "").trim(),
    item,
  ]),
);

function findDocumentNameOption(abbrOrUid) {
  const code = resolveDocumentNameAbbreviation(abbrOrUid);
  if (!code) return null;
  const known = abbreviationByCode.get(code);
  if (known) return known;
  return {
    sNo: `legacy-${code}`,
    description: `${code} (current)`,
    abbreviation: code,
  };
}

function buildDocumentNameOptions(currentAbbr) {
  const option = findDocumentNameOption(currentAbbr);
  if (option && !abbreviationByCode.has(currentAbbr)) {
    return [option, ...documentAbbreviations];
  }
  return documentAbbreviations;
}

// row._duidListeners is a tiny pub/sub so the DUID preview field can
// re-render when a sibling field (WBS, Document Identifier) changes.
// MRT's own row._valuesCache + setCreatingRow(row) does NOT work for this:
// setCreatingRow is called with the very same (in-place-mutated) row
// reference, so React's setState bails out via Object.is and nothing
// re-renders. Subscribing components force their own re-render instead.
function notifyDuidListeners(row, duid) {
  if (!Array.isArray(row._duidListeners)) return;
  row._duidListeners.forEach((fn) => {
    try {
      fn(duid);
    } catch {
      /* ignore */
    }
  });
}

function createDocumentIdentifierEdit(existingDocuments) {
  return function DocumentIdentifierEdit({ cell, column, row }) {
    const readStored = () =>
      String(
        row._valuesCache?.documentName ??
          row._valuesCache?.[column.id] ??
          row.original?.documentName ??
          row.original?.uid ??
          row.original?.department ??
          cell.getValue() ??
          "",
      ).trim();

    const [selected, setSelected] = React.useState(() =>
      findDocumentNameOption(readStored()),
    );

    React.useEffect(() => {
      if (!row._valuesCache) {
        row._valuesCache = {};
      }

      const abbr = selected?.abbreviation ?? "";
      if (!abbr) {
        row._valuesCache[column.id] = "";
        row._valuesCache.documentName = "";
        row._valuesCache.uid = "";
        notifyDuidListeners(row, "");
        return;
      }

      const ensured = ensureNumberedDocumentUid(
        {
          documentName: abbr,
          uid: row.original?.uid,
        },
        existingDocuments,
        { excludeId: row.original?.id },
      );
      const duid = ensured.documentName || "";
      // Keep column value as description for display; persist numbered DUID (e.g. GAD-001)
      row._valuesCache[column.id] = selected?.description ?? abbr;
      row._valuesCache.documentName = duid;
      row._valuesCache.uid = duid;
      notifyDuidListeners(row, duid);
    }, [selected, column.id, row]);

    const options = buildDocumentNameOptions(
      resolveDocumentNameAbbreviation(readStored()),
    );

    return (
      <Autocomplete
        size="small"
        fullWidth
        options={options}
        value={selected}
        onChange={(_event, option) => setSelected(option)}
        getOptionLabel={(option) => option?.description ?? ""}
        isOptionEqualToValue={(a, b) =>
          String(a?.abbreviation ?? "") === String(b?.abbreviation ?? "")
        }
        filterOptions={(opts, { inputValue }) => {
          const q = inputValue.trim().toUpperCase();
          if (!q) return opts;
          return opts.filter(
            (o) =>
              String(o.description ?? "")
                .toUpperCase()
                .includes(q) ||
              String(o.abbreviation ?? "")
                .toUpperCase()
                .includes(q),
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Document Identifier"
            placeholder="Select document type…"
            variant="standard"
          />
        )}
        sx={{ minWidth: 280, width: "100%" }}
      />
    );
  };
}

// WBS select (plant mode): writes the chosen projectId into row._valuesCache
// and notifies the DUID preview so it recomputes.
function createWbsProjectEdit(projectChoices) {
  return function WbsProjectEdit({ column, row }) {
    const readStored = () =>
      String(row._valuesCache?.[column.id] ?? row.original?.projectId ?? "").trim();

    const [value, setValue] = React.useState(readStored);

    React.useEffect(() => {
      if (!row._valuesCache) row._valuesCache = {};
      row._valuesCache[column.id] = value;
      notifyDuidListeners(row, "");
    }, [value, column.id, row]);

    return (
      <TextField
        select
        size="small"
        fullWidth
        label="WBS"
        variant="standard"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        {projectChoices.map((project) => (
          <MenuItem key={project.id} value={project.id}>
            {project.name}
          </MenuItem>
        ))}
      </TextField>
    );
  };
}

// WBS free-text field (non-plant mode): same notify-on-change wiring.
function WbsProjectNumberEdit({ column, row }) {
  const readStored = () =>
    String(row._valuesCache?.[column.id] ?? row.original?.projectNumber ?? "").trim();

  const [value, setValue] = React.useState(readStored);

  React.useEffect(() => {
    if (!row._valuesCache) row._valuesCache = {};
    row._valuesCache[column.id] = value;
    notifyDuidListeners(row, "");
  }, [value, column.id, row]);

  return (
    <TextField
      size="small"
      fullWidth
      label="WBS"
      variant="standard"
      value={value}
      onChange={(e) => setValue(e.target.value)}
    />
  );
}

// Read-only DUID preview. Subscribes to row._duidListeners so it re-renders
// whenever the WBS or Document Identifier fields change.
function createDocumentNumberPreview(fixedPlantName, projectChoices) {
  return function DocumentNumberPreview({ row }) {
    const [, forceTick] = React.useState(0);

    React.useEffect(() => {
      if (!Array.isArray(row._duidListeners)) row._duidListeners = [];
      const listener = () => forceTick((t) => t + 1);
      row._duidListeners.push(listener);
      return () => {
        row._duidListeners = (row._duidListeners || []).filter(
          (fn) => fn !== listener,
        );
      };
    }, [row]);

    const source = getRegisterDocSource(row);
    const wbsLabel = resolveModuleLabel(row, projectChoices);
    const hasWbs = wbsLabel.trim() !== "";
    const hasDocIdentifier = String(source.uid ?? "").trim() !== "";
    if (!hasWbs || !hasDocIdentifier) return null;

    const resolved = buildRegisterDocNumber(
      {
        ...source,
        projectNumber: fixedPlantName || String(source.projectNumber ?? "").trim(),
      },
      { wbsLabel },
    );
    return (
      <TextField
        label="DUID"
        value={resolved}
        variant="standard"
        fullWidth
        disabled
        InputLabelProps={{ shrink: true }}
      />
    );
  };
}

export const DocLsitInitialData = {
  companyName: "",
  projectNumber: "",
  documentName: "",
  uid: "",
  revision: "",
  numberOfSheets: "",
  documentNumber: "",
  description: "",
  projectId: "",
};

const headerCenterSx = {
  "& .Mui-TableHeadCell-Content": {
    justifyContent: "center",
  },
  textAlign: "center",
  fontWeight: 700,
};

const editLabelProps = (label) => ({
  muiEditTextFieldProps: { label },
});

const resolveModuleLabel = (row, projectChoices = []) => {
  const cache = row?._valuesCache ?? {};
  const original = row?.original ?? row ?? {};
  const projectId = String(
    cache.projectId ?? original.projectId ?? "",
  ).trim();
  const fromChoice = projectChoices.find((p) => String(p.id) === projectId);
  const label =
    fromChoice?.name ||
    cache.projectName ||
    original.projectName ||
    cache.projectNumber ||
    original.projectNumber ||
    "";
  return String(label ?? "").trim();
};

export const DocListColumns = (
  _allUsers,
  _type,
  {
    plant = false,
    plantName = "",
    projectChoices = [],
    existingDocuments = [],
  } = {},
) => {
  const fixedPlantName = String(plantName ?? "").trim();
  const DocumentIdentifierEdit =
    createDocumentIdentifierEdit(existingDocuments);
  const DocumentNumberPreview = createDocumentNumberPreview(
    fixedPlantName,
    projectChoices,
  );

  const moduleColumn = plant
    ? {
        id: "projectId",
        accessorKey: "projectId",
        header: "WBS",
        size: 28,
        enableEditing: true,
        Edit: createWbsProjectEdit(projectChoices),
        muiTableHeadCellProps: { sx: { ...headerCenterSx } },
        accessorFn: (row) => resolveModuleLabel(row, projectChoices),
        Cell: ({ row }) => {
          const v = resolveModuleLabel(row.original ?? row, projectChoices);
          return v !== "" ? v : "—";
        },
      }
    : {
        id: "projectNumber",
        accessorKey: "projectNumber",
        header: "WBS",
        size: 20,
        enableEditing: true,
        Edit: WbsProjectNumberEdit,
        muiTableHeadCellProps: { sx: { ...headerCenterSx } },
        accessorFn: (row) => String(row.projectNumber ?? "").trim(),
        Cell: ({ row }) => {
          const v = String(
            row.original?.projectNumber ?? row.projectNumber ?? "",
          ).trim();
          return v !== "" ? v : "—";
        },
      };

  const puidColumn = {
    id: "puid",
    header: "PUID",
    size: 24,
    enableEditing: false,
    muiTableHeadCellProps: { sx: { ...headerCenterSx } },
    accessorFn: (row) =>
      fixedPlantName || String(row.projectNumber ?? "").trim(),
    Cell: () => (fixedPlantName ? fixedPlantName : "—"),
  };

  return [
    puidColumn,
    moduleColumn,
    {
      id: "docNo",
      header: "Doc No",
      size: 28,
      enableEditing: true,
      Edit: DocumentIdentifierEdit,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#FFE0B2",
        },
      },
      accessorFn: (row) =>
        resolveDocumentUid(row) ||
        String(row.documentName ?? row.uid ?? "").trim(),
      Cell: ({ row }) => {
        const uid = resolveDocumentUid(
          row?.original ? getRegisterDocSource(row) : row,
        );
        return uid !== "" ? uid : "—";
      },
    },
    {
      id: "documentNumber",
      header: "DUID",
      size: 52,
      enableEditing: false,
      Edit: DocumentNumberPreview,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#C8E6C9",
        },
      },
      accessorFn: (row) =>
        buildRegisterDocNumber(
          {
            projectNumber:
              fixedPlantName || String(row.projectNumber ?? "").trim(),
            documentName: row.documentName,
            uid: row.uid,
          },
          { wbsLabel: resolveModuleLabel(row, projectChoices) },
        ),
      Cell: ({ row }) => {
        const source = getRegisterDocSource(row);
        const resolved = buildRegisterDocNumber(
          {
            ...source,
            projectNumber:
              fixedPlantName || String(source.projectNumber ?? "").trim(),
          },
          { wbsLabel: resolveModuleLabel(row, projectChoices) },
        );
        return resolved !== "" ? resolved : "—";
      },
    },
    {
      accessorKey: "revision",
      header: "Latest Revision",
      size: 15,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      ...editLabelProps("Latest Revision"),
    },
    {
      accessorKey: "numberOfSheets",
      header: "No of Sheets",
      size: 24,
      muiTableHeadCellProps: { sx: { ...headerCenterSx } },
      ...editLabelProps("No of Sheets"),
    },
    {
      id: "description",
      accessorFn: (row) => row.description ?? row.title ?? "",
      header: "Description",
      size: 36,
      muiTableHeadCellProps: {
        sx: {
          ...headerCenterSx,
          backgroundColor: "#BBDEFB",
        },
      },
      ...editLabelProps("Description"),
    },
  ];
};

const DocumentList = () => {
  const {
    allUsers,
    type,
    activeProjectId,
    plant,
    plantId,
    documentList,
    getDocList,
    documentListLoaded,
  } = useDCS();

  const [uuidProjectChoices, setUuidProjectChoices] = useState([]);
  const [plantName, setPlantName] = useState("");

  const documentListRef = useRef(documentList);
  documentListRef.current = documentList;
  const documentListLoadedRef = useRef(documentListLoaded);
  documentListLoadedRef.current = documentListLoaded;

  const defaultPlantProjectIdRef = useRef("");
  const plantNameRef = useRef("");
  const needsNetworkFetchRef = useRef(false);

  const filterDocumentsByType = useCallback(
    (list) => {
      if (!type || type === "all") return list || [];
      return (list || []).filter((doc) => doc.type === type);
    },
    [type],
  );

  const existingDocuments = useMemo(
    () => filterDocumentsByType(documentList || []),
    [documentList, filterDocumentsByType],
  );

  useEffect(() => {
    if (!plant || !plantId) {
      setPlantName("");
      return;
    }
    let cancelled = false;
    secondAxios
      .get(`/plant/${plantId}`)
      .then((res) => {
        if (!cancelled) setPlantName(res.data?.name || "");
      })
      .catch(() => {
        if (!cancelled) setPlantName("");
      });
    return () => {
      cancelled = true;
    };
  }, [plant, plantId]);

  useEffect(() => {
    if (!plant || !plantId) {
      setUuidProjectChoices([]);
      return undefined;
    }

    let cancelled = false;

    const loadProjects = async () => {
      try {
        const choices = await fetchPlantProjectChoices(plantId);
        if (!cancelled) setUuidProjectChoices(choices);
      } catch (e) {
        console.warn("Document Register project choices load failed:", e);
        if (!cancelled) setUuidProjectChoices([]);
      }
    };

    loadProjects();
    return () => {
      cancelled = true;
    };
  }, [plant, plantId]);

  const defaultPlantProjectId =
    uuidProjectChoices.length > 0 ? uuidProjectChoices[0].id : "";

  defaultPlantProjectIdRef.current = defaultPlantProjectId;
  plantNameRef.current = plantName;

  const columns = useMemo(
    () =>
      DocListColumns(allUsers, type, {
        plant,
        plantName,
        projectChoices: uuidProjectChoices,
        existingDocuments,
      }),
    [allUsers, type, plant, plantName, uuidProjectChoices, existingDocuments],
  );

  const parentId = plant && plantId ? plantId : activeProjectId;

  const getItems = useCallback(async () => {
    if (
      !needsNetworkFetchRef.current &&
      documentListLoadedRef.current &&
      documentListRef.current != null
    ) {
      return filterDocumentsByType(documentListRef.current);
    }

    needsNetworkFetchRef.current = false;
    const list = await getDocList();
    return filterDocumentsByType(list ?? documentListRef.current ?? []);
  }, [filterDocumentsByType, getDocList]);

  const withFixedProjectRef = useCallback((item) => {
    const name = String(plantNameRef.current ?? "").trim();
    if (!name) return item;
    return { ...item, projectNumber: name };
  }, []);

  const handleCreateItem = useCallback(
    async (createParentId, item) => {
      needsNetworkFetchRef.current = true;

      const existing = filterDocumentsByType(documentListRef.current || []);
      const withUid = withFixedProjectRef(
        ensureNumberedDocumentUid(item, existing),
      );

      if (plant && plantId) {
        const selectedProjectId =
          String(withUid.projectId ?? "").trim() ||
          defaultPlantProjectIdRef.current;
        if (!selectedProjectId) {
          throw new Error(
            "No WBS available for this document register row.",
          );
        }
        const wbsLabel = resolveModuleLabel(withUid, uuidProjectChoices);
        const { projectId, projectName, companyName, ...docData } = withUid;
        return createDocumentList(type, selectedProjectId, docData, {
          wbsLabel,
        });
      }
      return createDocumentList(type, createParentId, withUid);
    },
    [
      plant,
      plantId,
      type,
      filterDocumentsByType,
      withFixedProjectRef,
      uuidProjectChoices,
    ],
  );

  const handleUpdateItem = useCallback(
    async (updateDocument) => {
      needsNetworkFetchRef.current = true;
      const existing = filterDocumentsByType(documentListRef.current || []);
      const withUid = withFixedProjectRef(
        ensureNumberedDocumentUid(updateDocument, existing, {
          excludeId: updateDocument.id,
        }),
      );
      const wbsLabel = resolveModuleLabel(withUid, uuidProjectChoices);
      return updateDocumentList(withUid, { wbsLabel });
    },
    [filterDocumentsByType, withFixedProjectRef, uuidProjectChoices],
  );

  const handleDeleteItem = useCallback(async (id) => {
    needsNetworkFetchRef.current = true;
    return deleteDocumentList(id);
  }, []);

  const tableApi = useMemo(
    () => ({
      getItems,
      createItem: handleCreateItem,
      updateItem: handleUpdateItem,
      deleteItem: handleDeleteItem,
    }),
    [getItems, handleCreateItem, handleUpdateItem, handleDeleteItem],
  );

  const initialData = useMemo(
    () => ({
      ...DocLsitInitialData,
      projectNumber: plantName || "",
      ...(defaultPlantProjectId ? { projectId: defaultPlantProjectId } : {}),
    }),
    [defaultPlantProjectId, plantName],
  );

  return (
    <Box>
      {plant && plantName ? (
        <Typography variant="h4" color="darkBlue" sx={{ pb: 2 }}>
          {plantName}
        </Typography>
      ) : null}
      <EditableTable
        name="Doc Register"
        displayMode="modal"
        columns={columns}
        enableEditing={true}
        enableCreate={true}
        initialData={initialData}
        api={tableApi}
        parentId={parentId}
        additionalProps={{
          defaultColumn: {
            muiEditTextFieldProps: ({ column }) => ({
              label:
                typeof column.columnDef.header === "string"
                  ? column.columnDef.header
                  : undefined,
            }),
          },
        }}
      />
    </Box>
  );
};

export default DocumentList;
